import React from 'react';
import { Box, Button, ButtonBase, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { ChevronRight, Pencil, UserRound, X } from 'lucide-react';
import type { Employee } from '../../../store/slices/employeeSlice';
import { CountBadge, DefinitionList, PersonAvatar, SpecGrid } from '../../../components/ui';
import { focusRing, sbg } from '../../../theme/sbgTokens';
import { formatNumber, MISSING } from '../../../utils/format';
import { SelectVia, titleLineOf } from './orgChartModel';
import { DepartmentLabel, DeptDot, viaOf } from './OrgChartBits';
import { useFitToViewport } from './useMeasure';

export interface PersonDetailData {
  employee: Employee;
  /** The effective manager when they have an employee record. */
  manager: Employee | null;
  /** The free-text manager name on file when there is no record. */
  managerFallbackName: string | null;
  /** Everyone above this person, top of the org first. */
  reportingLine: Employee[];
  directReports: Employee[];
  totalInOrg: number;
  department: { label: string; color: string };
}

const firstNameOf = (name: string) => name.trim().split(/\s+/)[0] || name;

/** A person row that selects that person in the chart. */
const PersonButton: React.FC<{ person: Employee; onSelect: (id: string, via?: SelectVia) => void; size?: 24 | 32 }> = ({ person, onSelect, size = 24 }) => (
  <ButtonBase
    onClick={(event) => onSelect(person._id, viaOf(event))}
    aria-label={`Select ${person.name}`}
    sx={{
      width: '100%',
      justifyContent: 'flex-start',
      gap: 1.25,
      px: 1,
      py: 0.75,
      borderRadius: sbg.radius.md,
      textAlign: 'left',
      '&:hover': { bgcolor: sbg.bg.hover },
      '&.Mui-focusVisible': focusRing,
    }}
  >
    <PersonAvatar name={person.name} size={size} />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="labelMd" component="div" noWrap sx={{ color: sbg.text.primary }}>
        {person.name}
      </Typography>
      <Typography variant="body2" component="div" noWrap sx={{ color: sbg.text.secondary }}>
        {titleLineOf(person)}
      </Typography>
    </Box>
    <Box component="span" aria-hidden="true" sx={{ color: sbg.text.secondary, display: 'inline-flex' }}>
      <ChevronRight size={16} />
    </Box>
  </ButtonBase>
);

/** The record summary for the selected person: placement, reporting line and team. */
export const PersonDetailBody: React.FC<{
  data: PersonDetailData;
  onSelect: (id: string, via?: SelectVia) => void;
  /** Show avatar + department above the facts (the Sheet title carries only text). */
  showIdentity?: boolean;
}> = ({ data, onSelect, showIdentity = false }) => {
  const { employee, manager, managerFallbackName, reportingLine, directReports, totalInOrg, department } = data;
  const skipLevels = reportingLine.slice(0, -1);
  const team = employee.teamName && employee.teamName !== department.label ? employee.teamName : null;

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {showIdentity && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonAvatar name={employee.name} size={40} />
          <DepartmentLabel color={department.color} label={department.label} team={team} variant="body1" />
        </Box>
      )}

      <SpecGrid
        minColumnWidth={120}
        items={[
          { label: 'Direct reports', value: formatNumber(directReports.length) },
          { label: 'In their org', value: formatNumber(totalInOrg), caption: 'Everyone below them' },
        ]}
      />

      <DefinitionList
        items={[
          {
            label: 'Reports to',
            value: manager ? (
              <Link component="button" type="button" onClick={(event: React.MouseEvent) => onSelect(manager._id, viaOf(event))} sx={{ font: 'inherit', textAlign: 'left' }}>
                {manager.name}
              </Link>
            ) : (
              managerFallbackName || MISSING
            ),
          },
          {
            label: 'Department',
            value: (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <DeptDot color={department.color} />
                {department.label}
              </Box>
            ),
          },
          { label: 'Team', value: employee.teamName || MISSING },
          {
            label: 'Email',
            value: employee.email ? <Link href={`mailto:${employee.email}`}>{employee.email}</Link> : MISSING,
          },
        ]}
      />

      {skipLevels.length > 0 && (
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Typography variant="overline" component="h3" sx={{ color: sbg.text.secondary }}>
            Reporting line
          </Typography>
          <Box component="ol" aria-label={`${employee.name}'s reporting line`} sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 }}>
            {reportingLine.map((person) => (
              <Box component="li" key={person._id}>
                <PersonButton person={person} onSelect={onSelect} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'grid', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
            Direct reports
          </Typography>
          <CountBadge count={directReports.length} />
        </Box>
        {directReports.length > 0 ? (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25, mx: -1 }}>
            {directReports.map((report) => (
              <Box component="li" key={report._id}>
                <PersonButton person={report} onSelect={onSelect} />
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body1" sx={{ color: sbg.text.secondary }}>
            No one reports to {firstNameOf(employee.name)}.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/** View profile (secondary) and Edit placement (primary), shown only when the role allows them. */
export const PersonDetailActions: React.FC<{
  employee: Employee;
  canEdit: boolean;
  canViewProfile: boolean;
  onEdit: (employee: Employee) => void;
  onViewProfile: (id: string) => void;
}> = ({ employee, canEdit, canViewProfile, onEdit, onViewProfile }) => {
  if (!canEdit && !canViewProfile) return null;
  return (
    <>
      {canViewProfile && (
        <Button variant="outlined" startIcon={<UserRound size={16} />} onClick={() => onViewProfile(employee._id)}>
          View profile
        </Button>
      )}
      {canEdit && (
        <Button variant="contained" startIcon={<Pencil size={16} />} onClick={() => onEdit(employee)}>
          Edit placement
        </Button>
      )}
    </>
  );
};

/**
 * Inline detail rail beside the chart on wide pages (a Sheet takes over on
 * narrower ones). It is as tall as the chart card beside it and never taller
 * than the room left in the window, so the actions footer is always on screen.
 */
export const PersonDetailPanel: React.FC<{
  data: PersonDetailData;
  canEdit: boolean;
  canViewProfile: boolean;
  /** Receives the heading so the chart can move keyboard focus into the rail. */
  headingRef?: React.Ref<HTMLHeadingElement>;
  onClose: () => void;
  onSelect: (id: string, via?: SelectVia) => void;
  onEdit: (employee: Employee) => void;
  onViewProfile: (id: string) => void;
}> = ({ data, canEdit, canViewProfile, headingRef, onClose, onSelect, onEdit, onViewProfile }) => {
  const titleId = React.useId();
  const [panel, setPanel] = React.useState<HTMLElement | null>(null);
  useFitToViewport(panel);
  const { employee } = data;
  const team = employee.teamName && employee.teamName !== data.department.label ? employee.teamName : null;
  return (
    // The grid cell stretches to the chart card's height without its content adding to it.
    <Box sx={{ alignSelf: 'stretch', minWidth: 0, contain: 'size' }}>
      <Box
        ref={setPanel}
        component="aside"
        aria-labelledby={titleId}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Escape' && !event.defaultPrevented) {
            event.preventDefault();
            onClose();
          }
        }}
        sx={{
          position: 'sticky',
          top: 16,
          minWidth: 0,
          maxHeight: 'min(100%, var(--org-fit-height, 100%))',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: sbg.bg.surface,
          border: `1px solid ${sbg.border.default}`,
          borderRadius: sbg.radius.card,
        }}
      >
        <Box sx={{ flex: 'none', display: 'flex', alignItems: 'flex-start', gap: 1.5, pl: 2.5, pr: 1.5, py: 2, borderBottom: `1px solid ${sbg.border.subtle}` }}>
          <PersonAvatar name={employee.name} size={40} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              ref={headingRef}
              id={titleId}
              tabIndex={-1}
              variant="h3"
              component="h2"
              sx={{
                color: sbg.text.primary,
                overflowWrap: 'anywhere',
                borderRadius: sbg.radius.md,
                '&:focus': { outline: 'none' },
                '&:focus-visible': focusRing,
              }}
            >
              {employee.name}
            </Typography>
            <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary }}>
              {titleLineOf(employee)}
            </Typography>
            <DepartmentLabel color={data.department.color} label={data.department.label} team={team} variant="body2" sx={{ mt: 0.5 }} />
          </Box>
          <Tooltip title="Close details">
            <IconButton aria-label="Close details" onClick={onClose}>
              <X />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', p: 2.5 }}>
          <PersonDetailBody data={data} onSelect={onSelect} />
        </Box>
        {(canEdit || canViewProfile) && (
          <Box sx={{ flex: 'none', display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', px: 2.5, py: 1.5, borderTop: `1px solid ${sbg.border.subtle}` }}>
            <PersonDetailActions
              employee={employee}
              canEdit={canEdit}
              canViewProfile={canViewProfile}
              onEdit={onEdit}
              onViewProfile={onViewProfile}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
